import React, { useEffect, useMemo } from 'react';
import { PulseItemDTO, PulseSelection } from 'apis/DashboardAPI';
import { timeSince } from 'util/dateUtils';
import ClickableCard from 'ui/views/cards/ClickableCard';
import { CompanyOverviewSection, communityUrls, userUrls } from 'urls';
import { Link } from 'react-router-dom';
import Logo from 'ui/domain/Logo';
import CalendarIcon from 'ui/elements/icons/CalendarIcon';
import IconAvatar from 'ui/elements/icons/IconAvatar';
import DashboardMessage from '../messages/DashboardMessage';
import BookmarkIcon from 'ui/elements/icons/BookmarkIcon';
import CompanyProfileDialog from 'domain/companies/ProfileDialog/CompanyProfileDialog';
import EventDialog from 'domain/events/EventDialog';
import ActivityFromNetworkEmptyState from './ActivityFromNetworkEmptyState';
import { CommunityListItem } from 'types/company/community';
import InfoMessageInline from 'ui/views/messages/InfoMessageInline';
import InfoIcon from 'ui/elements/icons/InfoIcon';
import { UserFeedData } from 'pages/Dashboard/useUserFeedSWR';
import { CompanyListItem } from 'types/company';
import { useCompanyProfiles } from 'apis/CompanyAPI/companies/useCompanyProfiles';
import { notEmpty } from 'util/arrayUtils';
import { getOrUndefined } from 'util/resource';
import { sort } from 'ramda';
import { compareDesc } from 'date-fns';
import ShowUpdate from 'pages/Dashboard/updates/ShowUpdate';
import { bluePlanetTheme } from 'ui/theme';
import { ActivityType, TFilter } from './Pulse';
import { useProgressiveLoading } from 'hooks/useProgressiveLoading';
import { usePaginatedPulseSWR } from 'pages/Dashboard/usePaginatedPulseSWR';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { BODY_ID } from 'ui/views/layouts/BaseLayout/ScrollableBody';

export function getTitleAndHref(
  myCommunities: CommunityListItem[],
  item: PulseItemDTO,
): {
  title: React.ReactNode;
  href?: string;
  showCompanyIdInDialog?: { companyId: number; initialTab: CompanyOverviewSection };
  showEventIdInDialog?: UUID;
  logo: string | React.ReactNode;
} | null {
  switch (item.type) {
    case 'StartedFundingRound':
      return {
        title: `${item.company.name} started a new funding round`,
        showCompanyIdInDialog: { companyId: item.company.id, initialTab: 'profile' },
        logo: item.company.logoURL,
      };
    case 'ClosedFundingRound':
      return {
        title: `${item.company.name} closed their funding round`,
        showCompanyIdInDialog: { companyId: item.company.id, initialTab: 'profile' },
        logo: item.company.logoURL,
      };
    case 'UpdatedKpis':
      return {
        title: `${item.company.name} updated their metrics`,
        showCompanyIdInDialog: { companyId: item.company.id, initialTab: 'kpis' },
        logo: item.company.logoURL,
      };
    case 'UpdatedProfile':
      return {
        title: `${item.company.name} updated their profile`,
        showCompanyIdInDialog: { companyId: item.company.id, initialTab: 'profile' },
        logo: item.company.logoURL,
      };
    case 'UpdatedTeam':
      return {
        title: `${item.company.name} updated their team information`,
        showCompanyIdInDialog: { companyId: item.company.id, initialTab: 'team' },
        logo: item.company.logoURL,
      };
    case 'UpdatedPitchDeck':
      return {
        title: `${item.company.name} uploaded a new pitch deck`,
        showCompanyIdInDialog: { companyId: item.company.id, initialTab: 'profile' },
        logo: item.company.logoURL,
      };
    case 'PostedUpdate':
      const postedInCommunity = myCommunities
        .filter(c => c.roles.length > 0)
        .find(c => item.audienceCommunityIds.includes(c.details.id));

      return {
        title: postedInCommunity ? (
          <>
            {item.company.name} posted an update in{' '}
            <Link
              style={{ textDecoration: 'underline' }}
              to={communityUrls.overview(postedInCommunity.details.slug, 'updates')}
            >
              {postedInCommunity.details.name}
            </Link>
          </>
        ) : (
          `${item.company.name} posted an update`
        ),
        showCompanyIdInDialog: { companyId: item.company.id, initialTab: 'updates' },
        logo: item.company.logoURL,
      };
    case 'JoinedCommunity':
      return {
        title: (
          <>
            {item.company.name} joined{' '}
            <Link style={{ textDecoration: 'underline' }} to={communityUrls.overview(item.communitySlug)}>
              {item.communityName}
            </Link>
          </>
        ),
        showCompanyIdInDialog: { companyId: item.company.id, initialTab: 'profile' },
        logo: item.company.logoURL,
      };
    case 'CreatedEvent':
      return {
        title: `New event: ${item.title}`,
        showEventIdInDialog: item.eventId,
        logo: (
          <IconAvatar color="blue-light" size={{ width: '40px', height: '40px' }}>
            <CalendarIcon />
          </IconAvatar>
        ),
      };
  }
}

function getCompanyIds(feed: UserFeedData[]) {
  return feed.map(item => {
    switch (item.type) {
      case 'room':
        return item.roomUpdate.room.companyId;
      default:
        return undefined;
    }
  });
}

export default function PulseItems({
  feed,
  companies,
  communities,
  selectedFeed,
  activeFilter,
  newlyPostedUpdateId,
  enableScrollToUpdate,
}: {
  feed: UserFeedData[];
  companies: CompanyListItem[];
  communities: CommunityListItem[];
  selectedFeed: ActivityType;
  activeFilter: TFilter;
  newlyPostedUpdateId?: string;
  enableScrollToUpdate?: boolean;
}) {
  const [showCompanyInDialog, setCompanyToShowInDialog] = React.useState<
    { companyId: number; initialTab: CompanyOverviewSection } | undefined
  >();
  const [showEventIdInDialog, setEventIdToShowInDialog] = React.useState<UUID | undefined>();
  const { isLgDown } = useBreakpoints();
  const newlyPostedUpdateRef = React.useRef<HTMLDivElement | null>(null);

  // Scroll to newly posted update on screens under 1280px (only on dashboard home)
  useEffect(() => {
    if (newlyPostedUpdateId && isLgDown && enableScrollToUpdate) {
      // Use a longer timeout to ensure the ref is populated after render
      const timer = setTimeout(() => {
        if (newlyPostedUpdateRef.current) {
          const element = newlyPostedUpdateRef.current;
          const scrollContainer = document.getElementById(BODY_ID);

          if (scrollContainer) {
            const offsetInPx = 180;
            const containerTop = scrollContainer.getBoundingClientRect().top;
            const elementTop = element.getBoundingClientRect().top;
            const scrollPosition = scrollContainer.scrollTop + (elementTop - containerTop) - offsetInPx;
            scrollContainer.scrollTo({
              top: scrollPosition,
              behavior: 'smooth',
            });
          }
        }
      }, 300);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [newlyPostedUpdateId, isLgDown, enableScrollToUpdate]);

  // Use pulse data (no pagination for pulse feeds)
  const paginatedPulse = usePaginatedPulseSWR(selectedFeed);

  // Use paginated pulse data
  const pulseItems = paginatedPulse.allItems;

  // Memoize the sorted combined items to avoid recalculating on every render
  const allSortedItems = useMemo(() => {
    // For now, we'll need to get all data for sorting, but we'll limit rendering
    const updateElements =
      activeFilter === 'Only updates'
        ? []
        : feed.map((item, i) => ({
            element: null, // We'll render this later
            itemData: { type: 'update' as const, item, index: i },
            pinned: item.pinned === true,
            timestamp:
              item.type === 'boardroom'
                ? item.boardroomUpdate.update.createdAt
                : item.type === 'community'
                  ? item.communityUpdate.update.createdAt
                  : item.type === 'company'
                    ? item.companyUpdate.update.createdAt
                    : item.roomUpdate.update.createdAt,
          }));

    const activityElements =
      activeFilter === 'Only posts'
        ? []
        : pulseItems.map((item: PulseItemDTO, i: number) => ({
            element: null, // We'll render this later
            itemData: { type: 'activity' as const, item, index: i },
            pinned: false,
            timestamp: item.timestamp,
          }));

    // Posts the backend pinned stay on top; everything else is newest first
    return sort(
      (a, b) => Number(b.pinned) - Number(a.pinned) || compareDesc(new Date(a.timestamp), new Date(b.timestamp)),
      [...updateElements, ...activityElements].filter(notEmpty),
    );
  }, [feed, pulseItems, activeFilter]);

  // Use progressive loading hook with API pagination support
  const { visibleItemCount, loadMoreRef, hasMoreItems } = useProgressiveLoading({
    totalItems: allSortedItems.length,
    initialVisibleItems: 10,
    loadMoreBatchSize: 10,
    onLoadMore: paginatedPulse.loadMore,
    hasMoreData: paginatedPulse.hasMore,
  });

  // Only get data for visible items
  const visibleItems = useMemo(() => allSortedItems.slice(0, visibleItemCount), [allSortedItems, visibleItemCount]);

  // Get IDs only for visible items to optimize API calls
  const visibleFeedItems = useMemo(() => {
    return visibleItems.filter(item => item.itemData.type === 'update').map(item => item.itemData.item as UserFeedData);
  }, [visibleItems]);

  const companyIds = useMemo(() => getCompanyIds(visibleFeedItems).filter(notEmpty), [visibleFeedItems]);

  const { resource: companyProfileResource } = useCompanyProfiles(companyIds);
  const companyProfiles = getOrUndefined(companyProfileResource)?.values ?? [];

  // Render visible items only
  const renderedItems = useMemo(() => {
    return visibleItems
      .map(item => {
        if (item.itemData.type === 'update') {
          const feedItem = item.itemData.item as UserFeedData;
          const updateId =
            feedItem.type === 'boardroom'
              ? feedItem.boardroomUpdate.update.id
              : feedItem.type === 'community'
                ? feedItem.communityUpdate.update.id
                : feedItem.type === 'company'
                  ? feedItem.companyUpdate.update.id
                  : feedItem.roomUpdate.update.id;
          const isNewlyPosted = updateId === newlyPostedUpdateId;
          return (
            <ShowUpdate
              ref={isNewlyPosted ? newlyPostedUpdateRef : null}
              update={feedItem}
              key={`update-${item.itemData.index}`}
              communities={communities.map(c => c.details)}
              companies={companies}
              companyProfiles={companyProfiles}
              isNewlyPosted={isNewlyPosted}
            />
          );
        } else {
          const pulseItem = item.itemData.item as PulseItemDTO;
          const values = getTitleAndHref(communities, pulseItem);
          const image = values?.logo ?? '';

          return values ? (
            <ClickableCard
              avatar={typeof image === 'string' ? <Logo size={40} url={image} type="company" /> : image}
              title={values.title}
              key={`activity-${item.itemData.index}`}
              href={values.href}
              onClick={() => {
                if (values.showCompanyIdInDialog) setCompanyToShowInDialog(values.showCompanyIdInDialog);
                if (values.showEventIdInDialog) setEventIdToShowInDialog(values.showEventIdInDialog);
              }}
            >
              <span className="text-metadata">{timeSince(pulseItem.timestamp)}</span>
            </ClickableCard>
          ) : null;
        }
      })
      .filter(Boolean);
  }, [visibleItems, communities, companies, companyProfiles, newlyPostedUpdateId]);

  return (
    <>
      {/* We need this so that the page doesn't jump when switching between feed tabs */}
      <div style={{ minHeight: '85vh' }}>
        <>
          {allSortedItems.length === 0 && selectedFeed === 'From your network' && !paginatedPulse.loading ? (
            <ActivityFromNetworkEmptyState />
          ) : pulseItems.length === 0 && selectedFeed === 'From your collections' && !paginatedPulse.loading ? (
            <Link to={userUrls.discover}>
              <DashboardMessage
                topElement={
                  <IconAvatar color="blue-light" size={{ width: '60px', height: '60px' }}>
                    <BookmarkIcon style={{ fontSize: '30px' }} color="indigo"></BookmarkIcon>
                  </IconAvatar>
                }
              >
                <p>
                  Activity from your collections will appear here. Create your own shortlists and save companies that
                  you find interesting in order to keep tabs on their progress.
                </p>
              </DashboardMessage>
            </Link>
          ) : (
            <div>
              <div className="u-flex u-flex--column" style={{ gap: bluePlanetTheme.spacing(1) }}>
                {renderedItems}
              </div>
              {/* Load more sentinel */}
              {hasMoreItems && (
                <div ref={loadMoreRef} style={{ height: '20px', margin: '10px 0' }}>
                  <div className="text-metadata" style={{ textAlign: 'center' }}>
                    Loading more...
                  </div>
                </div>
              )}
              {allSortedItems.length < 30 && <PulseSelectionMessage selection={paginatedPulse.selection} />}
            </div>
          )}
        </>
      </div>
      {showCompanyInDialog && (
        <CompanyProfileDialog
          analyticsContext="dashboard-feed"
          companyId={showCompanyInDialog.companyId}
          analyticsCommunityId="none"
          initialTab={showCompanyInDialog.initialTab}
          onClose={() => setCompanyToShowInDialog(undefined)}
        />
      )}
      {showEventIdInDialog && (
        <EventDialog eventId={showEventIdInDialog} onClose={() => setEventIdToShowInDialog(undefined)} />
      )}
    </>
  );
}

function PulseSelectionMessage({ selection }: { selection: PulseSelection }) {
  const getMessage = () => {
    switch (selection) {
      case 'PlatformWide':
        return 'Updates and activity from all your networks will be presented here';
      case 'Mixed':
        return 'There are few relevant matches with activity at the moment, so we picked a few other popular for you. Stay tuned for more company activity, or change your investor preferences for other suggestions.';
      case 'InvestmentPreferences':
        return 'Great job, you found your way all the way here! Stay tuned for more company activity. Only selected activity last 30 days is available for you at this time.';
      default:
        return undefined;
    }
  };
  const message = getMessage();
  return message ? (
    <InfoMessageInline className="u-half-spacing-top" icon={<InfoIcon></InfoIcon>} color="blue">
      {message}
    </InfoMessageInline>
  ) : null;
}
